import { AlertTriangle, CornerUpRight, LocateFixed, Navigation } from 'lucide-react';

import { deriveRouteGuidance, formatGuidanceDistance } from '../../features/delivery/maps/routeGuidance';
import useStepGuidance from '../hooks/useStepGuidance';

const TONES = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-100',
  warning: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100',
  neutral: 'border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-100',
};

export default function RiderRouteGuidance({
  currentPosition, destination, encodedPolyline, mode, businessId, runId,
}) {
  const guidance = deriveRouteGuidance({ currentPosition, destination, encodedPolyline, mode });
  const Icon = guidance.tone === 'warning' ? AlertTriangle
    : guidance.status === 'NEAR_DESTINATION' ? LocateFixed : Navigation;

  // Step directions are only worth asking for once the position is good enough
  // to place the rider on a road. Below that the heading panel above already
  // explains why, and a request would be spent on a position we do not trust.
  const steps = useStepGuidance({
    businessId,
    runId,
    currentPosition,
    destination,
    enabled: Boolean(businessId && runId) && !['NO_LOCATION', 'POOR_ACCURACY', 'NO_DESTINATION'].includes(guidance.status),
  });

  return (
    <section className={`mt-4 rounded-2xl border p-4 ${TONES[guidance.tone] || TONES.neutral}`} aria-label="Route guidance" aria-live="polite">
      <div className="flex gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-semibold">{guidance.title}</p>
          <p className="mt-1 text-sm leading-5 opacity-80">{guidance.detail}</p>
        </div>
      </div>

      {steps.currentStep && (
        <div className="mt-3 flex items-start gap-3 border-t border-current/10 pt-3">
          <CornerUpRight className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            {/* Google returns the instruction already phrased for a driver, so
                it is shown as written rather than reworded into our own voice. */}
            <p className="font-semibold">{steps.currentStep.instruction}</p>
            {steps.remainingM != null && (
              <p className="mt-0.5 text-sm opacity-80">in {formatGuidanceDistance(steps.remainingM)}</p>
            )}
          </div>
        </div>
      )}

      {steps.unavailable && (
        // Directions are a convenience layered on a journey that works without
        // them, so this is a note rather than a warning.
        <p className="mt-3 border-t border-current/10 pt-3 text-sm opacity-70">
          Step directions are unavailable right now. The distance and heading above still apply,
          and you can open Google Maps for full navigation.
        </p>
      )}
    </section>
  );
}
